import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Modal from '../../components/Modals/Modal/Modal';
import EntryErrorModalContent from '../../components/Modals/EntryErrorModalContent/EntryErrorModalContent';
import { EntryErrorDefinition } from '../../types/types';
import { EntryErrorModalContextType } from '../../types/context';
import { buildEntryForceValue } from './utils/buildEntryForceValue';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('forceErrors');

// Entry value types safe to log raw — free-text ('string') can carry a
// PIN/code the backend asked for, 'card' a card identifier.
const ENTRY_VALUE_LOGGABLE_TYPES = new Set<EntryErrorDefinition['type']>([
  'int',
  'float',
  'date',
  'time',
  'datetime',
  'list',
]);

type entryActionFn = (
  value: string
) => void | ((value: string) => Promise<any>);

type cancelActionFn = () => void | (() => Promise<any>);

interface EntryErrorModalContextProviderProps {
  children: ReactNode;
}

const defaultValues: EntryErrorModalContextType = {
  isProvided: false,
  openEntryErrorModal: () => {
    throw new Error('openEntryErrorModal must be implemented');
  },
  isEntryModalOpen: false,
};

export const EntryErrorModalContext =
  createContext<EntryErrorModalContextType>(defaultValues);

const DEFAULT_ACTION = () => () => {};

const EntryErrorModalContextProvider = ({
  children,
}: EntryErrorModalContextProviderProps) => {
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [entry, setEntry] = useState<EntryErrorDefinition | null>(null);
  const [action, setAction] = useState<entryActionFn>(DEFAULT_ACTION);
  const [cancelAction, setCancelAction] =
    useState<cancelActionFn>(DEFAULT_ACTION);
  const promiseRef = useRef<{
    promise: Promise<any>;
    resolve: (value: any) => void;
  } | null>(null);

  const openEntryErrorModal = useCallback(
    (
      previousForce: string | undefined,
      message: string,
      entry: EntryErrorDefinition,
      entryModalAction: (forceValue: string) => void,
      entryModalCancelAction?: () => void
    ) => {
      /** @debugFeature forceErrors Logs an opened entry modal: the question, entry identifier, type and readers, and whether it chains onto previous answers. */
      log('entry modal opened', () => ({
        message,
        entryForce: entry.force,
        entryType: entry.type,
        readers: entry.readers,
        allowManual: entry.allowManual,
        chained: Boolean(previousForce),
        hasCancelAction: Boolean(entryModalCancelAction),
      }));

      setEntryModalOpen(true);
      setMessage(message);
      setEntry(entry);
      // The entered value is chained onto the accumulated force value; the
      // retry sends the whole chain in the X-ASW-FORCE header, same as force.
      setAction(
        () => (value: string) =>
          entryModalAction(
            buildEntryForceValue(entry.force, value, previousForce)
          )
      );
      // Always reset so a chained modal without its own cancel action doesn't
      // inherit the previous one.
      setCancelAction(() => entryModalCancelAction ?? DEFAULT_ACTION);
      // First entry error creates the promise; chained errors reuse it so
      // every nested handleApiError resolves together at final close instead
      // of awaiting an orphaned promise that never settles.
      if (!promiseRef.current) {
        let resolve!: (value: any) => void;
        const promise = new Promise((res) => {
          resolve = res;
        });
        promiseRef.current = { promise, resolve };
      }
      return promiseRef.current.promise;
    },
    []
  );

  const closeEntryErrorModal = useCallback((response: any = null) => {
    setEntryModalOpen(false);
    setMessage('');
    setEntry(null);
    setAction(DEFAULT_ACTION);
    setCancelAction(DEFAULT_ACTION);
    promiseRef.current?.resolve(response);
    promiseRef.current = null;
  }, []);

  const submitEntryValue: (value: string) => Promise<void> = useCallback(
    async (value: string) => {
      /** @debugFeature forceErrors Logs a submitted entry value retried in the accumulated X-ASW-FORCE value — the raw value is logged only for non-sensitive types (numbers, dates, list); free-text and card entries log its length only. */
      log('entry value submitted', () => ({
        entryForce: entry?.force,
        valueLength: value.length,
        ...(entry && ENTRY_VALUE_LOGGABLE_TYPES.has(entry.type)
          ? { value }
          : {}),
      }));

      const entryApiRes = await action(value);
      // Only the final close resolves the shared promise; nested calls unwinding
      // after the chain already resolved find promiseRef cleared and skip.
      if (promiseRef.current) closeEntryErrorModal(entryApiRes);
    },
    [action, entry]
  );

  const cancelModalAction: () => Promise<void> = useCallback(async () => {
    /** @debugFeature forceErrors Logs a cancelled entry modal — the awaiting caller resolves with null. */
    log('entry modal cancelled');

    if (cancelAction) await cancelAction();
    closeEntryErrorModal();
  }, [cancelAction, closeEntryErrorModal]);

  const context = useMemo(
    () => ({
      isProvided: true,
      openEntryErrorModal,
      isEntryModalOpen: entryModalOpen,
    }),
    [openEntryErrorModal, entryModalOpen]
  );

  return (
    <EntryErrorModalContext.Provider value={context}>
      {entryModalOpen && entry && (
        <Modal
          isOpen
          onClose={() => setEntryModalOpen(false)}
          content={
            <EntryErrorModalContent
              text={message}
              entry={entry}
              submitEntryValue={submitEntryValue}
              cancelModalAction={cancelModalAction}
            />
          }
        />
      )}
      {children}
    </EntryErrorModalContext.Provider>
  );
};

export default EntryErrorModalContextProvider;
